import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CivilAdviseSaveStep3 } from '../../types/civilAdviseApi';
import type { CivilAdviseWizardDraft } from '../../types/civilAdviseCaseRequest';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { ReviewUploadedFilesTable } from '../../components/wizard/ReviewUploadedFilesTable';
import { MultiFileReviewDocumentsTable } from '../wizard/civilAdvise/MultiFileReviewDocumentsTable';
import { useCivilAdviseLookups } from '../wizard/civilAdvise/useCivilAdviseLookups';
import { isOtherCorporateInstitute } from '../wizard/civilAdvise/civilAdviseHelpers';
import { CIVIL_ADVISE_FIELD_LABELS as L } from '../wizard/civilAdvise/civilAdviseFieldLabels';
import {
  apiOrLookupLabel,
  deliveryMethodDisplayFromStep3,
  submitterFromStep3,
} from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: CivilAdviseWizardDraft;
  submittedAt: string;
  step3?: CivilAdviseSaveStep3;
  hideUploadDocumentsStep?: boolean;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestCivilAdviseCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step3,
  hideUploadDocumentsStep = false,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useCivilAdviseLookups('review', draft.provinceId);

  const deliveryLabel = useMemo(
    () =>
      deliveryMethodDisplayFromStep3(
        draft as unknown as CriminalWizardDraft,
        lookups.deliveryMethods,
        step3,
        noData,
      ),
    [draft, lookups.deliveryMethods, step3, noData],
  );

  const submitter = useMemo(() => submitterFromStep3(step3), [step3]);

  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const connectedLabel = yesNoLabel(draft.hasConnectedMatters);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            <ReviewField label="Institute Type" value={instituteLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Intake Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label={L.hasPreviousAdvice} value={yesNoLabel(draft.hasPreviousAdvice)} />
            {draft.hasPreviousAdvice === 'yes' || draft.previousAdviceFiles.length > 0 ? (
              <div className="ccr-review-field is-full">
                <div className="ccr-review-lbl">{L.previousAdviceAttachment}</div>
                <ReviewUploadedFilesTable
                  files={draft.previousAdviceFiles}
                  emptyMessage={t('common.noPreviousAdviceDocuments')}
                />
              </div>
            ) : null}
            <ReviewField label={L.connectedCaseRelevant} value={yesNoLabel(draft.hasConnectedCaseRelevant)} />
            {draft.hasConnectedCaseRelevant === 'yes' ? (
              <ReviewField label={L.connectedCaseDetails} value={draft.connectedCaseDetails} span={2} />
            ) : null}
            <ReviewField label={L.caseNumber} value={draft.caseNumber} />
            <ReviewField label={L.connectedMatters} value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label={L.connectedCaseNumber} value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label={L.connectedAgdFileRef}
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Timeline">
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Agency Name" value={submitter.agency} />
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      {!hideUploadDocumentsStep ? (
        <ReviewSection step="Step 04" title="Upload Documents">
          <ReviewSubBlock>
            <MultiFileReviewDocumentsTable documents={draft.documents} />
          </ReviewSubBlock>
        </ReviewSection>
      ) : null}
    </div>
  );
}
